import { Username } from "@/models/User";

// Generic WebSocket message interface
export interface BaseWsMessage {
    action: string; 
    actionId: string;
    created: number; 
    userId: string;
    username: string;
    userDetails?: Username;
}

// Specific interface for regular chat or action messages
export interface LobbyMessage extends BaseWsMessage {
    roomId: string;
    data: {
        action: string;
        content: string; 
        gameStart?: number;
        gameEnd?: number;
        puzzleId?: string;
    };
}

// Specific interface for game updates
export interface GameMessage extends BaseWsMessage {
    gameId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any; // Retained as any for flexibility
}

// Type union for WebSocket messages
export type WsMessage = LobbyMessage | GameMessage;

export function parseWsMessage(eventData: string): [LobbyMessage[], GameMessage[]] {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parsedArray: BaseWsMessage[] = JSON.parse(eventData).map((item: any) => {
            const baseMessage: BaseWsMessage = {
                action: item.action,
                actionId: item.actionId,
                created: item.created,
                userId: item.userId,
                username: item.username,
                userDetails: item.userDetails
            };

            if (item.action === 'game_state') {
                return {
                    ...baseMessage,
                    gameId: item.gameId,
                    state: item.state,
                } as GameMessage;
            } else {
                // Parse the data string into an object
                const data = JSON.parse(item.data); // Parse the data JSON string
                
                return {
                    ...baseMessage,
                    roomId: item.roomId,
                    data: {
                        action: data.action || "",
                        content: data.message || "",
                        gameStart: data.gameStart || "",
                        gameEnd: data.gameEnd || "",
                        puzzleId: data.puzzleId || ""
                    },
                } as LobbyMessage;
            }
        });

        // Sort by `created` timestamp in descending order
        parsedArray.sort((a, b) => a.created - b.created);

        // Separate `LobbyMessage` and `GameMessage` objects
        const LobbyMessages: LobbyMessage[] = parsedArray.filter((item): item is LobbyMessage => item.action !== 'game_state');
        const GameMessages: GameMessage[] = parsedArray.filter((item): item is GameMessage => item.action === 'game_state');

        return [LobbyMessages, GameMessages];
    } catch (err) {
        console.log(eventData);
        console.log(err);
        return [[], []]
    }
}

export function initWebSocketConnection(
    address: string,
    userId: string,
    username: string,
    inviteRoomId: string,
    room_mode: string | null = null
): WebSocket {
    let socketAddress = ""
    if (inviteRoomId && inviteRoomId.length > 0) {
        socketAddress = `${address}?userId=${userId}&username=${username}&inviteRoomId=${inviteRoomId}`;
    } else if (room_mode) {
        socketAddress = `${address}?userId=${userId}&username=${username}&roomMode=${room_mode}`
    } else {
        socketAddress = `${address}?userId=${userId}&username=${username}`
    }
    console.log(socketAddress);
    const socket = new WebSocket(socketAddress);

    socket.onopen = (event: Event) => {
        const customEvent = new CustomEvent('xFrame:wsConnect', {
            cancelable: true,
            detail: event,
        });
        window.dispatchEvent(customEvent);
    };

    socket.onmessage = (event: MessageEvent) => {
        const customEvent = new CustomEvent('xFrame:wsMessage', {
            cancelable: true,
            detail: event,
        });
        window.dispatchEvent(customEvent);
    };

    socket.onclose = (event: Event) => {
        const customEvent = new CustomEvent('xFrame:wsClose', {
            cancelable: true,
            detail: event,
        });
        window.dispatchEvent(customEvent);
    };

    socket.onerror = (event: Event) => {
        const customEvent = new CustomEvent('xFrame:wsError', {
            cancelable: true,
            detail: event,
        });
        window.dispatchEvent(customEvent);
    };

    return socket;
}

export function isSocketOpen(socket: WebSocket): boolean {
    return socket.readyState === WebSocket.OPEN;
}

export function isSocketClosed(socket: WebSocket): boolean {
    return socket.readyState === WebSocket.CLOSED;
}

export function disconnectSocket(socket: WebSocket): void {
    socket.close();
}

export function sendSocketMessage(socket: WebSocket, action: string, message: string, userId: string, username: string): void {
    if (isSocketOpen(socket)) {
        const payload = JSON.stringify({
            action: action, 
            data: {
                "action": action,
                "userId": userId,
                "username": username,
                "message": message,
            },
        });
        socket.send(payload);
        const customEvent = new CustomEvent('xFrame:wsSend', {
            cancelable: true,
            detail: { message },
        });
        window.dispatchEvent(customEvent);
    }
}
